// Constant imports:
import { GET_SIGTI_API_URL } from '../../config';
// Service imports:
import { turnDateToSimpleTime } from '../../services/utils';
import { bugsnagNotify, fetchResponseToDetailedString } from '../../services/bugsnagUtils';
// Type imports:
import { AppDispatch } from '../../index'; // Use the root dispatch type from your store
import { PublicActions, REQUEST_LAST_PRICE_FAILURE, REQUEST_LAST_PRICE_SUCCESS, REQUEST_LAST_PRICE_WAITING, REQUEST_OPEN_CLOSING_PRICE_FAILURE, REQUEST_OPEN_CLOSING_PRICE_SUCCESS, REQUEST_OPEN_CLOSING_PRICE_WAITING, REQUEST_PREV_TRADING_DAY_FAILURE, REQUEST_PREV_TRADING_DAY_SUCCESS, REQUEST_PREV_TRADING_DAY_WAITING, REQUEST_PUBLIC_ORDERS_BY_LEVEL_FAILURE, REQUEST_PUBLIC_ORDERS_BY_LEVEL_SUCCESS, REQUEST_PUBLIC_ORDERS_BY_LEVEL_WAITING, REQUEST_PUBLIC_ORDERS_FAILURE, REQUEST_PUBLIC_ORDERS_SUCCESS, REQUEST_PUBLIC_ORDERS_WAITING, REQUEST_PUBLIC_TRADES_FAILURE, REQUEST_PUBLIC_TRADES_FOR_GROUP_FAILURE, REQUEST_PUBLIC_TRADES_FOR_GROUP_SUCCESS, REQUEST_PUBLIC_TRADES_FOR_GROUP_WAITING, REQUEST_PUBLIC_TRADES_SUCCESS, REQUEST_PUBLIC_TRADES_WAITING } from '../../types/redux/PublicTypes';
import { IPublicOrderByLevel, IPublicOrderHistoryEntry, IPublicTradeForGroup, IPublicTradeHistoryEntry } from '../../types/api/PublicTypes';
import { requestPublicClosingPriceQuery, requestPublicOpenClosingPriceQuery, requestPublicOrdersQuery, requestPublicPreviousTradingDayQuery, requestPublicTradesQuery } from '../queries/publicQueries';
import { requestPublicOrdersByLevelQuerry, requestPublicTradesForGroupQuerry } from '../queries/reportQueries';
import { OpenClosingPrice } from '../../types/api/OpenClosingPrice';

/* REQUEST PUBLIC ORDERS */
// ================================ REQUEST PUBLIC OPENING AND CLOSING PRICE ================================
const requestPublicOpenClosingPriceThunk = async (dispatch: AppDispatch, symbol: string, fromDate: string, toDate: string) => {
    // Set as waiting for data.
    dispatch(requestPublicOpenClosingPriceWaiting(symbol, fromDate, toDate));

    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = requestPublicOpenClosingPriceQuery();

    const queryObject = {
        query,
        variables: {
            fromDate,
            toDate,
            symbol
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });

        if (response.ok) {
            const responseBody = await response.json();
            if (!responseBody.data) {
                dispatch(requestPublicOpenClosingPriceFailure(symbol, fromDate, toDate, 'Session Expired'));
                return;
            }
            dispatch(requestPublicOpenClosingPriceSuccess(symbol, fromDate, toDate, responseBody.data.publicOpenClosingPrice));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Public Open Closing Price', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestPublicOpenClosingPriceFailure(symbol, fromDate, toDate, 'Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(requestPublicOpenClosingPriceFailure(symbol, fromDate, toDate, 'Network Error'));
    }
};

const requestPublicOpenClosingPriceWaiting = (symbol: string, fromDate: string, toDate: string): PublicActions => ({
    type: REQUEST_OPEN_CLOSING_PRICE_WAITING,
    payload: {
        symbol,
        fromDate,
        toDate
    }
});

const requestPublicOpenClosingPriceSuccess = (symbol: string, fromDate: string, toDate: string, openClosingPrice: Array<OpenClosingPrice>): PublicActions => ({
    type: REQUEST_OPEN_CLOSING_PRICE_SUCCESS,
    payload: {
        symbol,
        fromDate,
        toDate,
        openClosingPrice
    }
});

const requestPublicOpenClosingPriceFailure = (symbol: string, fromDate: string, toDate: string, error: string): PublicActions => ({
    type: REQUEST_OPEN_CLOSING_PRICE_FAILURE,
    payload: {
        symbol,
        fromDate,
        toDate,
        error
    }
});

export const requestPublicOpenClosingPrice =
    (symbol: string, fromDate: string, toDate: string) =>
    (dispatch: AppDispatch) =>
        requestPublicOpenClosingPriceThunk(dispatch, symbol, fromDate, toDate);

// ================================ REQUEST PUBLIC ORDERS ================================
const requestPublicOrdersThunk = async (dispatch: AppDispatch, symbol: string, currentTradingDay: string, accessToken: string) => {
    // Format time string.
    const simpleDateString = turnDateToSimpleTime(new Date(currentTradingDay));
    // Set as waiting for data.
    dispatch(requestPublicOrdersWaiting(symbol, simpleDateString));
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = requestPublicOrdersQuery();

    const queryObject = {
        query,
        variables: {
            fromDate: currentTradingDay,
            toDate: currentTradingDay,
            symbol: symbol
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });

        if (response.ok) {
            const responseBody = await response.json();
            if (!responseBody.data) {
                dispatch(requestPublicOrdersFailure(symbol, simpleDateString, 'Session Expired'));
                return;
            }
            dispatch(requestPublicOrdersSuccess(symbol, simpleDateString, responseBody.data.publicOrders));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Public Orders', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestPublicOrdersFailure(symbol, simpleDateString, 'Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(requestPublicOrdersFailure(symbol, simpleDateString, 'Network Error'));
    }
}

const requestPublicOrdersWaiting = (symbol: string, tradingDay: string): PublicActions => ({
    type: REQUEST_PUBLIC_ORDERS_WAITING,
    payload: {
        symbol,
        tradingDay
    }
});

const requestPublicOrdersSuccess = (symbol: string, tradingDay: string, orderHistory: Array<IPublicOrderHistoryEntry>): PublicActions => ({
    type: REQUEST_PUBLIC_ORDERS_SUCCESS,
    payload: {
        symbol,
        tradingDay,
        orderHistory
    }
});

const requestPublicOrdersFailure = (symbol: string, tradingDay: string, error: string): PublicActions => ({
    type: REQUEST_PUBLIC_ORDERS_FAILURE,
    payload: {
        symbol,
        tradingDay,
        error
    }
});

export const requestPublicOrders =
    (symbol: string, currentTradingDay: string, accessToken: string) =>
    (dispatch: AppDispatch) =>
    requestPublicOrdersThunk(dispatch, symbol, currentTradingDay, accessToken);




/* REQUEST PUBLIC TRADES */
// ================================ REQUEST PUBLIC TRADES ================================
const requestPublicTradesThunk = async (dispatch: AppDispatch, symbol: string, currentTradingDay: string, accessToken: string) => {
    // Format time string.
    const simpleDateString = turnDateToSimpleTime(new Date(currentTradingDay));
    // Set as waiting for data.
    dispatch(requestPublicTradesWaiting(symbol, simpleDateString));
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = requestPublicTradesQuery();

    const queryObject = {
        query,
        variables: {
            fromDate: currentTradingDay,
            toDate: currentTradingDay,
            symbol: symbol
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });
        if (response.ok) {
            const responseBody = await response.json();
            if (!responseBody.data) {
                dispatch(requestPublicTradesFailure(symbol, simpleDateString, 'Session Expired'));
                return;
            }
            dispatch(requestPublicTradesSuccess(symbol, simpleDateString, responseBody.data.publicTrades));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Public Trades', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestPublicTradesFailure(symbol, simpleDateString, 'Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(requestPublicTradesFailure(symbol, simpleDateString, 'Network Error'));
    }
}

const requestPublicTradesWaiting = (symbol: string, tradingDay: string): PublicActions => ({
    type: REQUEST_PUBLIC_TRADES_WAITING,
    payload: {
        symbol,
        tradingDay
    }
});

const requestPublicTradesSuccess = (symbol: string, tradingDay: string, tradeHistory: Array<IPublicTradeHistoryEntry>): PublicActions => ({
    type: REQUEST_PUBLIC_TRADES_SUCCESS,
    payload: {
        symbol,
        tradingDay,
        tradeHistory
    }
});

const requestPublicTradesFailure = (symbol: string, tradingDay: string, error: string): PublicActions => ({
    type: REQUEST_PUBLIC_TRADES_FAILURE,
    payload: {
        symbol,
        tradingDay,
        error
    }
});

export const requestPublicTrades =
    (symbol: string, currentTradingDay: string, accessToken: string) =>
    (dispatch: AppDispatch) =>
    requestPublicTradesThunk(dispatch, symbol, currentTradingDay, accessToken);




/* REQUEST PUBLIC TRADES FOR GROUP */
// ================================ REQUEST PUBLIC TRADES FOR GROUP ================================
const requestPublicTradesForGroupThunk = async (dispatch: AppDispatch, currentTradingDay: string, accessToken: string, groupId: string) => {
    // Format time string.
    const simpleDateString = turnDateToSimpleTime(new Date(currentTradingDay));
    // Set as waiting for data.
    dispatch(requestPublicTradesForGroupWaiting(simpleDateString));
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = requestPublicTradesForGroupQuerry();

    const queryObject = {
        query,
        variables: {
            day: currentTradingDay,
            symbol: groupId
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });
        if (response.ok) {
            const responseBody = await response.json();
            if (!responseBody.data) {
                dispatch(requestPublicTradesForGroupFailure(simpleDateString, 'Session Expired'));
                return;
            }
            dispatch(requestPublicTradesForGroupSuccess(simpleDateString, responseBody.data.publicTradesForGroup));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Public Trades For Group', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestPublicTradesForGroupFailure(simpleDateString, 'Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(requestPublicTradesForGroupFailure(simpleDateString, 'Network Error'));
    }
}

const requestPublicTradesForGroupWaiting = (tradingDay: string): PublicActions => ({
    type: REQUEST_PUBLIC_TRADES_FOR_GROUP_WAITING,
    payload: {
        tradingDay
    }
});

const requestPublicTradesForGroupSuccess = (tradingDay: string, publicGroupTrades: Array<IPublicTradeForGroup>): PublicActions => ({
    type: REQUEST_PUBLIC_TRADES_FOR_GROUP_SUCCESS,
    payload: {
        tradingDay,
        publicGroupTrades
    }
});

const requestPublicTradesForGroupFailure = (tradingDay: string, error: string): PublicActions => ({
    type: REQUEST_PUBLIC_TRADES_FOR_GROUP_FAILURE,
    payload: {
        tradingDay,
        error
    }
});

export const requestPublicTradesForGroup =
    (currentTradingDay: string, accessToken: string, groupId: string) =>
    (dispatch: AppDispatch) =>
    requestPublicTradesForGroupThunk(dispatch, currentTradingDay, accessToken, groupId);




/* REQUEST LAST PRICE */
// ================================ REQUEST CLOSEING PRICE ================================
const requestLastPriceThunk = async (dispatch: AppDispatch, symbol: string, currentTradingDay: string, accessToken: string) => {
    // Format time string.
    const simpleDateString = turnDateToSimpleTime(new Date(currentTradingDay));
    // Set as waiting for data.
    dispatch(requestLastPriceWaiting(symbol, simpleDateString));
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = requestPublicClosingPriceQuery();

    const queryObject = {
        query,
        variables: {
            day: currentTradingDay,
            symbol: symbol
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });
        if (response.ok) {
            const responseBody = await response.json();
            if (!responseBody.data) {
                dispatch(requestLastPriceFailure(symbol, simpleDateString, 'Session Expired'));
                return;
            }
            const closingPrice = responseBody.data.publicClosingPrice;
            if (Array.isArray(closingPrice) && closingPrice.length > 0) {
                const { official_last } = closingPrice[0];
                dispatch(requestLastPriceSuccess(symbol, simpleDateString, official_last));
            } else {
                dispatch(requestLastPriceSuccess(symbol, simpleDateString, null));
            }
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Last Price', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestLastPriceFailure(symbol, simpleDateString, 'Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(requestLastPriceFailure(symbol, simpleDateString, 'Network Error'));
    }
}

const requestLastPriceWaiting = (symbol: string, tradingDay: string): PublicActions => ({
    type: REQUEST_LAST_PRICE_WAITING,
    payload: {
        symbol,
        tradingDay
    }
});

const requestLastPriceSuccess = (symbol: string, tradingDay: string, lastPrice: number | null): PublicActions => ({
    type: REQUEST_LAST_PRICE_SUCCESS,
    payload: {
        symbol,
        tradingDay,
        lastPrice,
    }
});

const requestLastPriceFailure = (symbol: string, tradingDay: string, error: string): PublicActions => ({
    type: REQUEST_LAST_PRICE_FAILURE,
    payload: {
        symbol,
        tradingDay,
        error
    }
});

export const requestLastPrice =
    (symbol: string, currentTradingDay: string, accessToken: string) =>
    (dispatch: AppDispatch) =>
    requestLastPriceThunk(dispatch, symbol, currentTradingDay, accessToken);

/* REQUEST PREVIOUS TRADING DAY */
// ================================ REQUEST PREVIOUS TRADING DAY ================================
const requestPreviousTradingDayThunk = async (dispatch: AppDispatch, symbol: string, currentTradingDay: string, accessToken: string) => {
    // Format time string.
    const simpleDateString = turnDateToSimpleTime(new Date(currentTradingDay));
    // Set as waiting for data.
    dispatch(requestPreviousTradingDayWaiting(symbol, simpleDateString));
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = requestPublicPreviousTradingDayQuery();

    const queryObject = {
        query,
        variables: {
            day: currentTradingDay,
            symbol: symbol
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });
        if (response.ok) {
            const responseBody = await response.json();
            if (!responseBody.data) {
                dispatch(requestPreviousTradingDayFailure(symbol, simpleDateString, 'Session Expired'));
                return;
            }
            const prevTradingDay = responseBody.data.publicPreviousTradingDay?.day;
            if (typeof prevTradingDay === 'string') {
                dispatch(requestPreviousTradingDaySuccess(symbol, simpleDateString, prevTradingDay));
            } else {
                dispatch(requestPreviousTradingDaySuccess(symbol, simpleDateString, null));
            }
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Previous Trading Day', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestPreviousTradingDayFailure(symbol, simpleDateString, 'Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(requestPreviousTradingDayFailure(symbol, simpleDateString, 'Network Error'));
    }
}

const requestPreviousTradingDayWaiting = (symbol: string, tradingDay: string): PublicActions => ({
    type: REQUEST_PREV_TRADING_DAY_WAITING,
    payload: {
        symbol,
        tradingDay
    }
});

const requestPreviousTradingDaySuccess = (symbol: string, tradingDay: string, prevTradingDay: string | null): PublicActions => ({
    type: REQUEST_PREV_TRADING_DAY_SUCCESS,
    payload: {
        symbol,
        tradingDay,
        prevTradingDay
    }
});

const requestPreviousTradingDayFailure = (symbol: string, tradingDay: string, error: string): PublicActions => ({
    type: REQUEST_PREV_TRADING_DAY_FAILURE,
    payload: {
        symbol,
        tradingDay,
        error
    }
});

export const requestPreviousTradingDay =
    (symbol: string, currentTradingDay: string, accessToken: string) =>
    (dispatch: AppDispatch) =>
    requestPreviousTradingDayThunk(dispatch, symbol, currentTradingDay, accessToken);



/* REQUEST PUBLIC ORDERS BY LEVEL */

const requestPublicOrdersByLevelThunk = async (dispatch: AppDispatch, symbol: string, currentTradingDay: string, accessToken: string) => {
    // Format time string.
    const simpleDateString = turnDateToSimpleTime(new Date(currentTradingDay));
    // Set as waiting for data.
    dispatch(requestPublicOrdersByLevelWaiting(symbol, simpleDateString));
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = requestPublicOrdersByLevelQuerry();

    const queryObject = {
        query,
        variables: {
            day: simpleDateString,
            symbol: symbol
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });
        if (response.ok) {
            const responseBody = await response.json();
            if (!responseBody.data) {
                dispatch(requestPublicOrdersByLevelFailure(symbol, simpleDateString, 'Session Expired'));
                return;
            }
            dispatch(requestPublicOrdersByLevelSuccess(symbol, simpleDateString, responseBody.data.publicOrdersByLevel));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Public Orders', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestPublicOrdersByLevelFailure(symbol, simpleDateString, 'Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(requestPublicOrdersByLevelFailure(symbol, simpleDateString, 'Network Error'));
    }
}

const requestPublicOrdersByLevelWaiting = (symbol: string, tradingDay: string): PublicActions => ({
    type: REQUEST_PUBLIC_ORDERS_BY_LEVEL_WAITING,
    payload: {
        symbol,
        tradingDay
    }
});

const requestPublicOrdersByLevelSuccess = (symbol: string, tradingDay: string, publicOrdersByLevel: Array<IPublicOrderByLevel>): PublicActions => ({
    type: REQUEST_PUBLIC_ORDERS_BY_LEVEL_SUCCESS,
    payload: {
        symbol,
        tradingDay,
        publicOrdersByLevel
    }
});

const requestPublicOrdersByLevelFailure = (symbol: string, tradingDay: string, error: string): PublicActions => ({
    type: REQUEST_PUBLIC_ORDERS_BY_LEVEL_FAILURE,
    payload: {
        symbol,
        tradingDay,
        error
    }
});

export const requestPublicOrdersByLevel =
    (symbol: string, currentTradingDay: string, accessToken: string) =>
    (dispatch: AppDispatch) =>
    requestPublicOrdersByLevelThunk(dispatch, symbol, currentTradingDay, accessToken);

